/**
 * web/src/hooks/mutations/_optimistic.ts
 * Shared optimistic-mutation scaffolding, extracted from hooks that had
 * copy-pasted the identical cancel → snapshot → patch → rollback → invalidate
 * skeleton (F4):
 *   - gateOptimisticHandlers: the approve/skip pair (remove gate from the
 *     pending list, then invalidate the gate/workflow/stats caches).
 *   - useOptimisticMatrixMutation: the four admin-access matrix mutations
 *     (toggle / role / slack-id / delete), which differ only in how they
 *     patch the cached AdminAccessMatrix.
 */
import { useMutation, useQueryClient, type QueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { toastApiError } from '../../lib/api';
import { qk } from '../../lib/queryKeys';
import type { AdminAccessMatrix } from '../../api/types';

/* ───────────────────────── Gate approve / skip ───────────────────────── */

/** Shared variable shape for the optimistic gate mutations (approve, skip). */
export interface GateMutationVars {
  workflowId: string;
  stepIndex: number;
  edits: Record<string, unknown>;
  /** Optional per-call toast override (e.g. "Email sent — workflow resumed"). */
  successMessage?: string;
}

interface GateOptimisticContext {
  previous: unknown;
}

/**
 * Builds the `onMutate` / `onError` / `onSettled` handlers shared by
 * useApproveGate and useSkipGate. Optimistically drops the gate from the
 * pending list, rolls back on error, and re-syncs gate/workflow/stats caches.
 *
 * @param qc the active QueryClient
 * @param opts.defaultSuccess success toast when the caller passes no override
 * @param opts.errorFallback toast shown when a non-ApiClientError rejects
 */
export function gateOptimisticHandlers(
  qc: QueryClient,
  opts: { defaultSuccess: string; errorFallback: string },
) {
  return {
    onMutate: async (vars: GateMutationVars): Promise<GateOptimisticContext> => {
      await qc.cancelQueries({ queryKey: qk.gates.pending() });
      const previous = qc.getQueryData(qk.gates.pending());
      qc.setQueryData<unknown[] | undefined>(qk.gates.pending(), (old) => {
        if (!Array.isArray(old)) return old;
        return old.filter((g) => {
          const item = g as { workflowId?: string; stepIndex?: number };
          return !(item.workflowId === vars.workflowId && item.stepIndex === vars.stepIndex);
        });
      });
      toast.success(vars.successMessage ?? opts.defaultSuccess);
      return { previous };
    },
    onError: (err: unknown, _vars: GateMutationVars, ctx: GateOptimisticContext | undefined) => {
      if (ctx?.previous !== undefined) qc.setQueryData(qk.gates.pending(), ctx.previous);
      toastApiError(err, opts.errorFallback);
    },
    onSettled: (_data: unknown, _err: unknown, vars: GateMutationVars) => {
      qc.invalidateQueries({ queryKey: qk.gates.pending() });
      qc.invalidateQueries({ queryKey: qk.workflow.root(vars.workflowId) });
      qc.invalidateQueries({ queryKey: qk.workflows.root() });
      qc.invalidateQueries({ queryKey: qk.stats.root() });
    },
  };
}

/* ───────────────────── Admin access-matrix mutations ───────────────────── */

interface MatrixOptimisticContext {
  previous: AdminAccessMatrix | undefined;
}

/**
 * Optimistic mutation over the admin-access matrix. Cancels in-flight reads,
 * snapshots the matrix, applies `patch(prev, vars)` immediately, rolls back on
 * error, and re-syncs from the server on settle. The four admin mutations
 * differ only in `patch` (and whether they fire a success toast).
 *
 * @param mutationFn the API call
 * @param patch pure transform producing the next matrix from the current one
 * @param opts.errorFallback toast shown when a non-ApiClientError rejects
 * @param opts.successMessage optional success toast (cell-flip mutations omit it)
 */
export function useOptimisticMatrixMutation<TVars>(
  mutationFn: (vars: TVars) => Promise<unknown>,
  patch: (prev: AdminAccessMatrix, vars: TVars) => AdminAccessMatrix,
  opts: { errorFallback: string; successMessage?: string },
) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn,
    onMutate: async (vars: TVars): Promise<MatrixOptimisticContext> => {
      await qc.cancelQueries({ queryKey: qk.adminAccess() });
      const previous = qc.getQueryData<AdminAccessMatrix>(qk.adminAccess());
      if (previous) qc.setQueryData<AdminAccessMatrix>(qk.adminAccess(), patch(previous, vars));
      return { previous };
    },
    onSuccess: () => {
      if (opts.successMessage) toast.success(opts.successMessage);
    },
    onError: (err: unknown, _vars: TVars, ctx: MatrixOptimisticContext | undefined) => {
      if (ctx?.previous) qc.setQueryData(qk.adminAccess(), ctx.previous);
      toastApiError(err, opts.errorFallback);
    },
    onSettled: () => {
      qc.invalidateQueries({ queryKey: qk.adminAccess() });
    },
  });
}
